<template>
  <AuthLayout
    mode="register"
    sidebar-title="Verify your account"
    :sidebar-text="sidebarText"
    :support-phone="supportPhone"
    :progress-steps="progressSteps"
    :progress-current-step="2"
    :title="panelTitle"
    :subtitle="panelSubtitle"
    :header-centered="isCenteredHeader"
    :card="view !== 'choose_method'"
    :classic="classic"
    classic-register-url=""
  >
    <template v-if="classic" #classic-title>
      Thank you for registering with <span class="classic-auth__accent">RemoniHealth</span>
    </template>
    <template v-if="classic && view === 'email_wait'" #classic-subtitle>
      <span class="classic-auth__accent">Step 2.</span> Email verification.
    </template>

    <div v-if="view === 'choose_method'" class="auth-register__verify">
      <p class="auth-login__panel-subtitle">Validate your account through:</p>
      <div v-if="classic" class="classic-verify-methods" role="radiogroup" aria-label="Verification method">
        <label class="classic-verify-methods__option">
          Email
          <input v-model="selectedMethod" type="radio" :value="VERIFY_METHOD.EMAIL">
          <span aria-hidden="true"></span>
        </label>
        <label class="classic-verify-methods__option">
          Mobile phone
          <input v-model="selectedMethod" type="radio" :value="VERIFY_METHOD.SMS">
          <span aria-hidden="true"></span>
        </label>
      </div>
      <div v-else class="auth-register__roles" role="radiogroup" aria-label="Verification method">
        <AuthRoleOption
          title="Email"
          description="We'll send a verification link to your email."
          icon="mail"
          :active="selectedMethod === VERIFY_METHOD.EMAIL"
          @select="selectedMethod = VERIFY_METHOD.EMAIL"
        />
        <AuthRoleOption
          title="Mobile phone"
          description="We'll send a 6-digit code to your mobile phone."
          icon="smartphone"
          :active="selectedMethod === VERIFY_METHOD.SMS"
          @select="selectedMethod = VERIFY_METHOD.SMS"
        />
      </div>
      <span v-if="error" class="auth-login__error-block" role="alert">{{ error }}</span>
      <div class="auth-login__actions">
        <AuthButton :loading="loading" :disabled="!selectedMethod" @click="emit('select-method', selectedMethod)">
          {{ classic ? 'Select' : 'Continue' }}
        </AuthButton>
      </div>
    </div>

    <div v-else-if="view === 'email_wait'" class="auth-register__verify auth-register__verify--centered">
      <p class="auth-register__verify-text">
        <template v-if="classic">
          A verification email has been sent to the email you provided. Please check your Spam mailbox as well.
          If you did not receive the email,
          <button
            class="classic-verification__inline-action"
            type="button"
            :disabled="loading || isCoolingDown"
            @click="emit('resend-email')"
          >
            {{ isCoolingDown ? `request another in ${countdown}` : 'click here to request another' }}
          </button>.
        </template>
        <template v-else>
          A verification email has been sent to <strong>{{ email }}</strong>.
          Please check your Spam mailbox as well.
        </template>
      </p>
      <p v-if="resendFeedback" class="auth-login__hint">{{ resendFeedback }}</p>
      <span v-if="error" class="auth-login__error-block" role="alert">{{ error }}</span>
      <p v-if="classic" class="auth-register__verify-text">
        Do you want to change the verification method?
        <button
          class="classic-verification__inline-action"
          type="button"
          :disabled="loading"
          @click="view = 'choose_method'"
        >
          Change to mobile phone
        </button>
      </p>
      <div v-else class="auth-login__actions">
        <AuthButton
          variant="ghost"
          :loading="loading"
          :disabled="isCoolingDown"
          @click="emit('resend-email')"
        >
          {{ isCoolingDown ? `Resend in ${countdown}` : 'Resend verification email' }}
        </AuthButton>
        <AuthButton variant="ghost" :disabled="loading" @click="view = 'choose_method'">
          Change verification method
        </AuthButton>
      </div>
    </div>

    <div v-else-if="view === 'phone_otp'" class="auth-register__verify">
      <p class="auth-register__verify-text">
        <template v-if="classic">Enter the code that was sent to the mobile phone you specified.</template>
        <template v-else>Enter the 6-digit code sent to <strong>{{ phone }}</strong>.</template>
      </p>
      <OtpInput v-model="code" :error="error" legend="Phone verification code" />
      <div class="auth-login__actions">
        <AuthButton :loading="loading" :disabled="code.length < 6" @click="verifyPhoneCode">
          Verify
        </AuthButton>
        <AuthButton
          variant="ghost"
          :loading="loading"
          :disabled="isCoolingDown"
          @click="emit('resend-phone')"
        >
          {{ isCoolingDown ? `Resend in ${countdown}` : 'Resend code' }}
        </AuthButton>
        <AuthButton variant="ghost" :disabled="loading" @click="view = 'choose_method'">
          {{ classic ? 'Change to email' : 'Change verification method' }}
        </AuthButton>
      </div>
    </div>
  </AuthLayout>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import AuthLayout from '../../shared/components/AuthLayout.vue'
import AuthButton from '../../shared/components/AuthButton.vue'
import AuthRoleOption from '../../shared/components/AuthRoleOption.vue'
import OtpInput from '../../shared/components/OtpInput.vue'
import { PRACTICE_STEPS, PATIENT_STEPS, VERIFY_METHOD } from '../constants/steps.js'

const props = defineProps({
  flow:               { type: String, default: 'practice' },
  needsMethodSelection: { type: Boolean, default: true },
  verificationMethod: { type: Number, default: null },
  email:              { type: String, default: '' },
  phone:              { type: String, default: '' },
  loading:            { type: Boolean, default: false },
  error:              { type: String, default: '' },
  resendFeedback:     { type: String, default: '' },
  isCoolingDown:      { type: Boolean, default: false },
  countdown:          { type: String, default: '' },
  supportPhone:       { type: String, default: '815-908-2020' },
  classic:            { type: Boolean, default: false },
})

const emit = defineEmits(['select-method', 'resend-email', 'resend-phone', 'verify-phone', 'verified'])

const progressSteps = computed(() => (props.flow === 'patient' ? PATIENT_STEPS : PRACTICE_STEPS))
const sidebarText = computed(() => (
  props.flow === 'patient'
    ? 'Confirm your identity to complete patient registration.'
    : 'Confirm your identity to complete practice registration.'
))
const selectedMethod = ref(null)
const code = ref('')
const view = ref('choose_method')
let isVerifyingPhoneCode = false

const panelTitle = computed(() => {
  if (props.classic) return 'Thank you for registering with RemoniHealth'
  if (view.value === 'choose_method') return 'Account verification'
  if (view.value === 'phone_otp') return 'Verify your mobile phone'
  return 'Email verification'
})

const panelSubtitle = computed(() => {
  if (props.classic) return ''
  if (view.value === 'choose_method') return 'Choose how you\'d like to verify your identity.'
  if (view.value === 'phone_otp') return 'Enter the code we sent to your mobile phone.'
  return 'Check your inbox for a verification link.'
})

const isCenteredHeader = computed(() => view.value === 'email_wait')

const resolveView = () => {
  if (props.needsMethodSelection || !props.verificationMethod) {
    view.value = 'choose_method'
    return
  }

  view.value = props.verificationMethod === VERIFY_METHOD.SMS ? 'phone_otp' : 'email_wait'
}

watch(() => [props.needsMethodSelection, props.verificationMethod], resolveView, { immediate: true })

watch(() => props.error, (error) => {
  if (!error || !isVerifyingPhoneCode) return

  isVerifyingPhoneCode = false
  code.value = ''
}, { flush: 'sync' })

const verifyPhoneCode = () => {
  if (code.value.length < 6 || props.loading) return

  isVerifyingPhoneCode = true
  emit('verify-phone', code.value)
}

onMounted(resolveView)
</script>

<style scoped>
.classic-verify-methods {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 22px auto;
  max-width: 240px;
}

.classic-verify-methods__option {
  align-items: center;
  color: #333;
  cursor: pointer;
  display: flex;
  font-size: 16px;
  gap: 15px;
  justify-content: flex-start;
  position: relative;
}

.classic-verify-methods__option input {
  opacity: 0;
  position: absolute;
}

.classic-verify-methods__option span {
  border: 2px solid #c6c6c6;
  border-radius: 50%;
  box-sizing: border-box;
  flex: 0 0 20px;
  height: 20px;
  order: -1;
  width: 20px;
}

.classic-verify-methods__option input:checked + span {
  border: 6px solid #ff7f5d;
}

.classic-verification__inline-action {
  background: none;
  border: 0;
  color: #564495;
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-decoration: underline;
}

.classic-verification__inline-action:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
